import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { UserPlus, Trash2, Shield, HardHat, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "technicien" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Utilisateur créé");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "technicien" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer ${u.name} ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-4xl">Utilisateurs</h1>
          <p className="mt-1 text-sm text-slate-500">Gérez les techniciens et administrateurs.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="h-11 gap-2 bg-amber-500 font-semibold text-slate-900 hover:bg-amber-400">
              <UserPlus className="h-4 w-4" /> Nouvel utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Créer un utilisateur</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Input placeholder="Nom complet" value={form.name} data-testid="user-name"
                onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" />
              <Input type="email" placeholder="Email" value={form.email} data-testid="user-email"
                autoComplete="off" name="new-user-email"
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" />
              <Input type="text" placeholder="Mot de passe" value={form.password} data-testid="user-password"
                autoComplete="new-password" name="new-user-password"
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11" />
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-11" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="technicien">Technicien</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving || !form.email || !form.password || !form.name}
                data-testid="save-user-btn" className="bg-slate-900 text-white dark:bg-white dark:text-slate-900">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Mobile: stacked cards */}
      <div className="space-y-3 md:hidden">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-card-${u.id}`}
            className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {u.name?.slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-slate-900 dark:text-white">{u.name}</p>
                <p className="truncate text-xs text-slate-500">{u.email}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(u)} disabled={u.id === me.id}
                data-testid={`delete-user-${u.id}`} className="shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 dark:border-slate-800">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                u.role === "admin" ? "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}>
                {u.role === "admin" ? <Shield className="h-3 w-3" /> : <HardHat className="h-3 w-3" />}
                {u.role === "admin" ? "Admin" : "Technicien"}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Actif</span>
                <Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)}
                  data-testid={`toggle-active-${u.id}`} disabled={u.id === me.id} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop/tablet: table */}
      <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 md:block">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/50">
            <tr>
              <th className="px-5 py-3 font-semibold">Utilisateur</th>
              <th className="px-5 py-3 font-semibold">Rôle</th>
              <th className="px-5 py-3 font-semibold">Actif</th>
              <th className="px-5 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      {u.name?.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">{u.name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    u.role === "admin" ? "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}>
                    {u.role === "admin" ? <Shield className="h-3 w-3" /> : <HardHat className="h-3 w-3" />}
                    {u.role === "admin" ? "Admin" : "Technicien"}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)}
                    data-testid={`toggle-active-desktop-${u.id}`} disabled={u.id === me.id} />
                </td>
                <td className="px-5 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => remove(u)} disabled={u.id === me.id}
                    data-testid={`delete-user-desktop-${u.id}`} className="text-red-600 hover:bg-red-50 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
